/* eslint-disable react/prop-types */
import * as DialogPrimitives from '@radix-ui/react-dialog';
import clsx from 'clsx';
import * as React from 'react';

export type DialogProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitives.Root
> & {
  isDismissable?: boolean;
};
export const focusRing = [
  // base
  'outline outline-offset-2 outline-0 focus-visible:outline-2',
  // outline color
  'outline-blue-500 dark:outline-blue-500',
];

const Dialog = ({
  isDismissable = true,
  onOpenChange,
  ...props
}: DialogProps) => {
  return (
    <DialogPrimitives.Root
      {...props}
      onOpenChange={(open) => {
        if (!isDismissable && !open) {
          return;
        }
        onOpenChange?.(open);
      }}
    />
  );
};

Dialog.displayName = 'Dialog';

const DialogTrigger = DialogPrimitives.Trigger;

DialogTrigger.displayName = 'DialogTrigger';

const DialogClose = DialogPrimitives.Close;

DialogClose.displayName = 'DialogClose';

const DialogPortal = DialogPrimitives.Portal;

DialogPortal.displayName = 'DialogPortal';

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitives.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitives.Overlay>
>(function DialogOverlay({ className, ...props }, forwardedRef) {
  return (
    <DialogPrimitives.Overlay
      ref={forwardedRef}
      className={clsx([
        // Base
        'group fixed isolate z-10 inset-0 flex w-screen justify-center overflow-y-auto bg-zinc-950/25 px-2 py-2 focus:outline-0 dark:bg-zinc-950/50 sm:px-6 sm:py-8 lg:px-8 lg:py-16',

        // Transition
        'data-[state=open]:animate-dialogOverlayShow',
      ])}
      {...props}
    />
  );
});

const sizes = {
  xs: 'sm:max-w-xs',
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
  '3xl': 'sm:max-w-3xl',
  '4xl': 'sm:max-w-4xl',
  '5xl': 'sm:max-w-5xl',
  '6xl': 'sm:max-w-6xl',
  '7xl': 'sm:max-w-7xl',
  full: 'sm:max-w-full',
};

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitives.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitives.Content> & {
    size?: keyof typeof sizes;
    bleed?: boolean;
    inheritStyles?: boolean;
  }
>(function DialogContent(
  { className, bleed = false, inheritStyles = true, size = 'lg', ...props },
  forwardedRef,
) {
  return (
    <DialogPortal>
      <DialogOverlay>
        <div
          className={clsx([
            'fixed isolate inset-0 w-screen overflow-y-auto pt-6 sm:pt-0',
          ])}
        >
          <div className='grid pointer-events-none min-h-full grid-rows-[1fr_auto] justify-items-center sm:grid-rows-[1fr_auto_3fr] sm:p-4'>
            <div className={clsx([sizes[size], 'row-start-2 w-full min-w-0'])}>
              <DialogPrimitives.Content
                ref={forwardedRef}
                className={clsx(
                  className,
                  sizes[size],
                  // layout
                  'row-start-2',
                  inheritStyles && [
                    // Bleed
                    !bleed && 'p-6',
                    // base
                    'w-full min-w-0 bg-white rounded-md border shadow-lg outline-none ring-1 ring-zinc-950/10 [--gutter:theme(spacing.8)] dark:bg-zinc-900 dark:ring-white/10 sm:mb-auto forced-colors:outline',
                    // border color
                    'border-gray-200 dark:border-gray-900 ',
                    // background color
                    ' bg-white dark:bg-[#090E1A]',
                  ],
                  // transition
                  'data-[state=open]:animate-dialogContentShow',
                  focusRing,
                )}
                {...props}
              />
            </div>
          </div>
        </div>
      </DialogOverlay>
    </DialogPortal>
  );
});

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div className={clsx('flex flex-col gap-y-1', className)} {...props} />
  );
};

DialogHeader.displayName = 'DialogHeader';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitives.Title>
>(function DialogTitle({ className, ...props }, forwardedRef) {
  return (
    <DialogPrimitives.Title
      ref={forwardedRef}
      className={clsx(
        // base
        'text-lg font-semibold',
        // text color
        'text-gray-900 dark:text-gray-50',
        className,
      )}
      {...props}
    />
  );
});

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitives.Description>
>(function DialogDescription({ className, ...props }, forwardedRef) {
  return (
    <DialogPrimitives.Description
      ref={forwardedRef}
      className={clsx('text-gray-500 dark:text-gray-500', className)}
      {...props}
    />
  );
});

export function DialogBody({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) {
  return <div {...props} className={clsx(className, 'mt-6')} />;
}

export function DialogActions({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'mt-8 flex flex-col-reverse items-center justify-end gap-3 *:w-full sm:flex-row sm:*:w-auto',
      )}
    />
  );
}

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      className={clsx(
        'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
        className,
      )}
      {...props}
    />
  );
};

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
};
